import { ApiError, OfflineError } from '../../api/client.js';
import { createTask, updateTask, rescheduleTask, completeTask, deleteTask } from '../../api/tasks.js';
import { createCourse, updateCourse, deleteCourse } from '../../api/courses.js';
import { createHabit, updateHabit, deleteHabit, logHabit, unlogHabit } from '../../api/habits.js';
import { useOffline } from '../../stores/offline.js';
import { useToast } from '../../stores/toast.js';
import { useTasks } from '../../stores/tasks.js';
import { useCourses } from '../../stores/courses.js';
import { useHabits } from '../../stores/habits.js';
import { type QueuedOp, describeOp, isTempId } from './queue.js';

/**
 * Le rejeu de la file hors-ligne. Voir [ADR 0010](../../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md).
 *
 * Appelle les modules `api/` directement, jamais les stores : passer par les stores
 * relancerait leur propre logique de mise en file, et une entrée rejouée se remettrait
 * en file au premier hoquet.
 */

/** Rend l'identifiant réel quand l'opération est une création, `null` sinon. */
async function perform(op: QueuedOp): Promise<string | null> {
  switch (op.kind) {
    case 'task.create':     return (await createTask(op.input)).taskId;
    case 'task.update':     await updateTask(op.id, op.patch); return null;
    case 'task.reschedule': await rescheduleTask(op.id, op.dueDate); return null;
    case 'task.complete':   await completeTask(op.id); return null;
    case 'task.delete':     await deleteTask(op.id); return null;
    case 'course.create':   return (await createCourse(op.input)).courseId;
    case 'course.update':   await updateCourse(op.id, op.patch); return null;
    case 'course.delete':   await deleteCourse(op.id); return null;
    case 'habit.create':    return (await createHabit(op.input)).habitId;
    case 'habit.update':    await updateHabit(op.id, op.patch); return null;
    case 'habit.delete':    await deleteHabit(op.id); return null;
    case 'habit.log':       await logHabit(op.id, op.date); return null;
    case 'habit.unlog':     await unlogHabit(op.id, op.date); return null;
  }
}

/**
 * Trois issues possibles, et une seule est un échec.
 *
 * - `drop` : le serveur a refusé pour une raison qui ne passera pas avec le temps
 *   (404 sur une entité supprimée entre-temps, 400 sur un état devenu incohérent).
 *   Sur une app mono-utilisateur, un 404 veut dire que l'état serveur a déjà gagné.
 * - `stop` : la file est conservée intacte et le drain reprendra plus tard. C'est le
 *   cas du 401 — un token expiré fait tout échouer, et jeter la file parce qu'il faut
 *   se reconnecter serait la pire perte possible — du 5xx, qui est un problème de
 *   serveur et non de donnée, et du retour hors-ligne en plein drain.
 */
export function classify(err: unknown): 'drop' | 'stop' {
  if (err instanceof OfflineError) return 'stop';
  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 429 || err.status >= 500) return 'stop';
    return 'drop';
  }
  return 'stop';
}

function reportDropped(dropped: string[]): void {
  if (dropped.length === 0) return;
  const message =
    dropped.length === 1
      ? `Action perdue · ${dropped[0]}`
      : `${dropped.length} actions perdues · ${dropped.join(', ')}`;
  useToast.getState().show({ message });
}

/**
 * Vide la file, en FIFO strict et **une requête à la fois** : l'ordre porte du sens
 * (créer puis cocher), et le parallélisme rouvrirait le 429 d'API Gateway que
 * `habits.setDay` évite déjà avec son `MAX_IN_FLIGHT`.
 */
export async function drain(): Promise<void> {
  const offline = useOffline.getState();
  if (offline.draining || offline.pending.length === 0) return;

  const expired = offline.pruneExpired();
  reportDropped(expired.map((e) => describeOp(e.op)));

  offline.setDraining(true);
  const dropped: string[] = [];
  let progressed = false;

  try {
    for (;;) {
      const queue = useOffline.getState().pending;
      const entry = queue[0];
      if (!entry) break;

      // Un identifiant temporaire encore là au moment de jouer l'opération signifie
      // que sa création n'a jamais abouti : il n'y a rien à adresser côté serveur.
      if (isTempId(entry.op.id) && !entry.op.kind.endsWith('.create')) {
        useOffline.getState().dropOrphans(entry.op.id);
        dropped.push(describeOp(entry.op));
        continue;
      }

      const sent = JSON.stringify(entry.op);
      try {
        const realId = await perform(entry.op);
        progressed = true;

        // L'entrée a pu être **repliée pendant son propre vol** : le réseau flanche, la
        // requête en cours aboutit, et la mutation que l'utilisateur vient de faire a
        // fusionné ses champs dans cette même entrée. La retirer par `entryId` perdrait
        // ces champs sans le moindre échec — précisément ce que le repli existe pour
        // empêcher. On ne la retire donc que si elle n'a pas bougé ; sinon on la laisse
        // et le tour suivant rejoue la version fusionnée. Sûr, parce que seules des
        // opérations idempotentes se replient (jamais une création ni une suppression).
        const current = useOffline.getState().pending.find((e) => e.entryId === entry.entryId);
        if (!current || JSON.stringify(current.op) === sent) {
          const rest = useOffline.getState().pending.filter((e) => e.entryId !== entry.entryId);
          useOffline.getState().setPending(rest);
        }

        if (realId && isTempId(entry.op.id)) {
          useOffline.getState().remap(new Map([[entry.op.id, realId]]));
        }
      } catch (err) {
        if (classify(err) === 'stop') break;
        dropped.push(describeOp(entry.op));
        if (isTempId(entry.op.id)) {
          // La création a échoué : tout ce qui visait ce `tmp_` part avec elle,
          // sans un toast par orpheline.
          useOffline.getState().dropOrphans(entry.op.id);
        } else {
          const rest = useOffline.getState().pending.filter((e) => e.entryId !== entry.entryId);
          useOffline.getState().setPending(rest);
        }
      }
    }
  } finally {
    useOffline.getState().setDraining(false);
  }

  reportDropped(dropped);

  // La file vide, l'état local n'a plus autorité : on resynchronise sur le serveur.
  if (progressed && useOffline.getState().pending.length === 0) {
    await Promise.allSettled([
      useTasks.getState().reloadCurrent(),
      useCourses.getState().load(),
      useHabits.getState().load(),
    ]);
  }
}

/**
 * Branche le rejeu : une fois au démarrage, puis à chaque retour du réseau.
 *
 * Il n'y a pas de Background Sync sur iOS Safari, donc rien ne peut vider la file
 * app fermée — le démarrage *est* le second déclencheur, pas un filet.
 */
export function startReplay(): () => void {
  void drain();
  const onOnline = (): void => void drain();
  window.addEventListener('online', onOnline);
  return () => window.removeEventListener('online', onOnline);
}
